import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { licensingAPI, License } from '../../services/api';
import LicenseStatusBadge from './LicenseStatusBadge';

export interface LicenseSelectorProps {
  licenseType: 'song' | 'actor';
  selectedLicenseIds: string[];
  onSelectionChange: (licenseIds: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

const LicenseSelector: React.FC<LicenseSelectorProps> = ({
  licenseType,
  selectedLicenseIds,
  onSelectionChange,
  label,
  placeholder,
  disabled = false,
  fullWidth = true,
}) => {
  const [options, setOptions] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLicenses, setSelectedLicenses] = useState<License[]>([]);

  const defaultLabel = licenseType === 'song' ? 'Song Licenses' : 'Actor Licenses';
  const defaultPlaceholder = licenseType === 'song'
    ? 'Search song licenses...'
    : 'Search actor licenses...';

  useEffect(() => {
    const fetchLicenses = async () => {
      setLoading(true);
      try {
        const { licenses } = await licensingAPI.list(licenseType);
        setOptions(licenses);

        const selected = licenses.filter(l => selectedLicenseIds.includes(l.license_id));
        setSelectedLicenses(selected);
      } catch (error) {
        console.error('Failed to load licenses:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLicenses();
  }, [licenseType]);

  useEffect(() => {
    const selected = options.filter(l => selectedLicenseIds.includes(l.license_id));
    setSelectedLicenses(selected);
  }, [selectedLicenseIds, options]);

  const handleChange = (_event: React.SyntheticEvent, value: License[]) => {
    setSelectedLicenses(value);
    onSelectionChange(value.map(l => l.license_id));
  };

  return (
    <Autocomplete
      multiple
      options={options}
      value={selectedLicenses}
      onChange={handleChange}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.license_id === value.license_id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || defaultLabel}
          placeholder={placeholder || defaultPlaceholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.license_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {option.name}
          </Typography>
          <LicenseStatusBadge status={option.status} size="small" />
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((license, index) => (
          <Chip
            {...getTagProps({ index })}
            key={license.license_id}
            label={license.name}
            size="small"
            color={license.status === 'expired' ? 'error' : license.status === 'expiring_soon' ? 'warning' : 'default'}
            variant="outlined"
          />
        ))
      }
    />
  );
};

export default LicenseSelector;
